import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { alpha } from '@mui/material/styles';
import { Avatar, Box, Button, Chip, Grid, Stack, Typography } from '@mui/material';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';

import { appURL } from '../Data/baseURL';
import {
  BackToHome,
  GlassCard,
  PublicContainer,
  PublicPage,
  PublicPageHero,
  SectionHeading,
  publicPalette,
} from '../Public/PublicLayout';

const ProductCard = ({ icon, title, eyebrow, description, features, action, accent }) => (
  <GlassCard sx={{ height: '100%', p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column' }}>
    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
      <Avatar sx={{ width: 56, height: 56, color: accent, backgroundColor: alpha(accent, 0.12) }}>{icon}</Avatar>
      <Box>
        <Typography sx={{ color: accent, fontSize: 12, fontWeight: 900, letterSpacing: '.12em', textTransform: 'uppercase' }}>{eyebrow}</Typography>
        <Typography component="h2" sx={{ color: publicPalette.text, fontSize: 28, fontWeight: 900, letterSpacing: '-.03em' }}>{title}</Typography>
      </Box>
    </Stack>
    <Typography sx={{ mb: 3, color: publicPalette.muted, fontSize: 17, lineHeight: 1.75 }}>{description}</Typography>
    <Stack spacing={1.25} sx={{ mb: 4, flexGrow: 1 }}>
      {features.map((feature) => (
        <Stack key={feature} direction="row" spacing={1.1} alignItems="center">
          <VerifiedRoundedIcon sx={{ color: accent, fontSize: 19 }} />
          <Typography sx={{ color: publicPalette.text, fontWeight: 650 }}>{feature}</Typography>
        </Stack>
      ))}
    </Stack>
    {action}
  </GlassCard>
);

const Products = () => {
  const { t } = useTranslation();

  return (
    <PublicPage>
      <Helmet>
        <title>CONTDEPLATA — {t('general.our_products')}</title>
        <meta name="description" content={t('public.products_meta', { defaultValue: 'Explore cloud software products for invoicing and transport management.' })} />
        <link rel="canonical" href="/apps/products" />
      </Helmet>

      <PublicPageHero
        eyebrow={t('general.our_products')}
        title={t('public.products_title', { defaultValue: 'Focused software for finance and transport operations' })}
        description={t('public.products_intro', { defaultValue: 'Two connected products built by a team that understands invoicing, logistics, and the daily pressure of document-heavy workflows.' })}
        actions={<BackToHome />}
      />

      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: publicPalette.background }}>
        <PublicContainer>
          <SectionHeading
            eyebrow={t('public.product_suite', { defaultValue: 'Product suite' })}
            title={t('public.products_section_title', { defaultValue: 'Use the right workspace for the job' })}
            description={t('public.products_section_text', { defaultValue: 'Start with streamlined online invoicing, or move into a broader transport management environment when operations require it.' })}
            sx={{ mb: 6 }}
          />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ProductCard
                icon={<ReceiptLongRoundedIcon />}
                accent={publicPalette.primary}
                eyebrow={t('public.invoicing_product', { defaultValue: 'Online invoicing' })}
                title="CONTDEPLATA.MD"
                description={t('general.description_contdeplata')}
                features={[
                  t('base.powerfull_invoicing'),
                  t('base.send_by_email'),
                  t('base.personalize_with_logo'),
                  t('base.handle_customer'),
                ]}
                action={
                  <Button
                    component={NavLink}
                    to={`${appURL}/signup`}
                    endIcon={<ArrowOutwardRoundedIcon />}
                    sx={{ width: 'fit-content', px: 2.5, py: 1.25, color: '#03111A', borderRadius: 2.5, textTransform: 'none', fontWeight: 900, background: `linear-gradient(110deg, ${publicPalette.primary}, ${publicPalette.accent})` }}
                  >
                    {t('button.signup_for_free')}
                  </Button>
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ProductCard
                icon={<LocalShippingRoundedIcon />}
                accent={publicPalette.secondary}
                eyebrow={t('public.transport_product', { defaultValue: 'Transport management' })}
                title="CARGOS.MD"
                description={t('base.product_description_cargosmd')}
                features={[
                  t('public.cargos_feature_1', { defaultValue: 'Operational workflows for freight teams' }),
                  t('public.cargos_feature_2', { defaultValue: 'Transport document and partner management' }),
                  t('public.cargos_feature_3', { defaultValue: 'Built around logistics-industry processes' }),
                  t('public.cargos_feature_4', { defaultValue: 'Cloud access for distributed teams' }),
                ]}
                action={
                  <Button
                    component="a"
                    href="https://cargos.md"
                    target="_blank"
                    rel="noreferrer"
                    endIcon={<ArrowOutwardRoundedIcon />}
                    variant="outlined"
                    sx={{ width: 'fit-content', px: 2.5, py: 1.25, color: publicPalette.text, borderColor: alpha(publicPalette.secondary, 0.55), borderRadius: 2.5, textTransform: 'none', fontWeight: 900 }}
                  >
                    {t('public.visit_cargos', { defaultValue: 'Visit CARGOS.MD' })}
                  </Button>
                }
              />
            </Grid>
          </Grid>

          <GlassCard sx={{ mt: 4, p: { xs: 3, md: 4 }, textAlign: 'center' }}>
            <Chip label={t('public.shared_expertise', { defaultValue: 'Shared domain expertise' })} sx={{ mb: 2, color: publicPalette.accent, backgroundColor: alpha(publicPalette.accent, 0.09), fontWeight: 800 }} />
            <Typography sx={{ mx: 'auto', maxWidth: 820, color: publicPalette.muted, fontSize: { xs: 16, md: 18 }, lineHeight: 1.75 }}>
              {t('public.products_bridge', { defaultValue: 'Both products are shaped by practical experience in transport, logistics, billing, and customer communication—so the interface follows real work rather than forcing teams into generic processes.' })}
            </Typography>
          </GlassCard>
        </PublicContainer>
      </Box>
    </PublicPage>
  );
};

export default Products;
